import { useTranslations } from "../../contexts/Translation";
import Section from "../Section";
import { useEffect, useState } from "react";
import { useApiData } from "../../contexts/ApiData";
import Button from "../Button/Button";
import Input, { Select } from "./Input";
import { ImprovementGoal, ValidationError, ValidationResult } from "../../types";
import ErrorText from "./ErrorText";
import { useNavigate } from "react-router-dom";
import ImprovementSelector from "./ImprovementSelector/ImprovementSelector";
import { useImprovementGoal } from "../../contexts/ImprovementGoal";
import GroupAccess from "./GroupAccess";
import HeadingWithInfo from "../HeadingWithInfo/HeadingWithInfo";
import GoalHelp from "./HelpComponents/GoalHelp";
import AiCoach from "../AiCoach/AiCoach";
import styles from "./FormStyles.module.css";
import { MAX_GOAL_NAME, MAX_GOAL_TEXT } from "../../lib/textLimits";

type ComponentProps = {
  isEdit?: boolean;
  surveyId?: string | null;
  onSuccess?: () => void;
};

const ImprovementGoalForm = ({ isEdit = false, surveyId = null, onSuccess }: ComponentProps) => {
  const t = useTranslations("improvementGoals");
  const { goal, improvementData, originalSurvey, questions, reloadGoal } = useImprovementGoal();
  const { post, put } = useApiData();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const navigate = useNavigate();
  const initialData: ImprovementGoal = {
    id: goal?.id || crypto.randomUUID(),
    endDate: goal?.endDate?.substring(0, 10) || new Date(Date.now() + 26 * 7 * 24 * 3600000).toISOString().substring(0, 10),
    categories: goal?.categories || [],
    goal: goal?.goal || "",
    name: goal?.name || "",
    plan: goal?.plan || "",
    result: goal?.result || "",
    situation: goal?.situation || "",
    originalSurvey: goal?.originalSurvey || surveyId || "",
    accessibleBy: goal?.accessibleBy || "me",
    groupsWithAccess: goal?.groupsWithAccess || originalSurvey?.groupsWithAccess || [],
    surveyType: originalSurvey?.surveyType || "long-term",
  };
  const [formData, setFormData] = useState<ImprovementGoal>(initialData);
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);

  useEffect(() => {
    setAvailableGroups(originalSurvey?.groupsWithAccess || []);
    setFormData({ ...formData, surveyType: originalSurvey?.surveyType || "long-term" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalSurvey]);

  const validateData = ({ name, endDate, originalSurvey }: ImprovementGoal): ValidationResult => {
    const errors = [];
    // const inOneWeek = (new Date(Date.now() + 7*24*3600000)).toISOString().substring(0, 10);

    if (!name) {
      errors.push({ name: "name", message: t("fieldRequired") });
    }

    if (!endDate) {
      errors.push({ name: "endDate", message: t("fieldRequired") });
    }
    if (!originalSurvey) {
      errors.push({ name: "form", message: t("missingOriginalSurvey") });
    }

    return { errors, success: errors.length === 0 };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrors(errors.filter((err) => err.name !== e.target.name));
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const updateCategories = (selected: string[]) => {
    setFormData({ ...formData, categories: selected });
  };

  const setGroupsWithAccess = (groups: string[]) => {
    setFormData({ ...formData, groupsWithAccess: groups });
    setErrors(errors.filter((error) => error.name !== "groupsWithAccess"));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateData(formData);
    if (validationResult.success) {
      const result = isEdit ? await put(`/goals/${formData.id}`, formData) : await post(`/goals/${formData.id}`, formData);
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
      } else {
        if (isEdit) {
          await reloadGoal();
          onSuccess?.();
        } else {
          navigate(`/improvement/${formData.id}#0`);
        }
      }
    } else {
      setErrors(validationResult.errors || []);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const getAccessibleByOptions = () => {
    const originalAccess = originalSurvey?.accessibleBy;

    const accessOptions = [
      { value: "me", text: t("accessibleByMe") },
      { value: "supporters", text: t("accessibleBySupporters") },
      { value: "selectedGroups", text: t("surveyForm.accessibleBySelectedGroups") },
      { value: "org", text: t("accessibleByOrganization") },
    ];
    const originalOptionIdx = accessOptions.findIndex((o) => o.value === originalAccess);
    const availableAccessOptions = accessOptions.filter((_o, idx) => idx <= originalOptionIdx);
    return availableAccessOptions;
  };

  // In edit mode the form sits inside a card in the narrow main column, under the goal's own H1.
  const Wrapper = isEdit ? "div" : Section;

  return (
    <Wrapper {...(isEdit ? {} : { span: 8 as const })}>
      {isEdit ? (
        <HeadingWithInfo level={2} heading={t("editHeading")} info={<GoalHelp isEdit />} />
      ) : (
      <div className={styles.headerRow}>
        <HeadingWithInfo level={1} heading={t("heading")} info={<GoalHelp isEdit />} />
        <div className={styles.headerAside}>
          {surveyId && (
            <AiCoach
              coachingType='createGoal'
              surveyId={surveyId}
              onComplete={(result) => {
                if (result) {
                  setFormData((prev) => ({ ...prev, ...result }));
                }
              }}
              onError={(msg) => setErrors([{ name: "form", message: msg }])}
            />
          )}
        </div>
      </div>
      )}
      <form className={styles.form} onSubmit={handleSubmit}>
        {errors.filter((e) => e.name !== "form").length > 0 && <ErrorText variant='form' text={t("formErrors")} />}
        <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />

        <div className={styles.fieldRow}>
          <Input error={errors.find((e) => e.name === "name")} name='name' maxLength={MAX_GOAL_NAME} handleChange={handleChange} label={t("name")} value={formData.name} />
        </div>

        {improvementData && (
          <ImprovementSelector
            helpText={t("help.improvementSelector")}
            questions={questions}
            handleChange={updateCategories}
            selected={formData.categories}
            scores={improvementData}
          />
        )}

        <div className={styles.fieldRow}>
          <Input
            error={errors.find((e) => e.name === "endDate")}
            type='date'
            name='endDate'
            helpText={t("help.goalEndDate")}
            handleChange={handleChange}
            label={t("endDate")}
            value={formData.endDate}
          />
        </div>

        <Input type='textarea' name='goal' maxLength={MAX_GOAL_TEXT} helpText={t("help.goal")} handleChange={handleChange} label={t("goal")} value={formData.goal} />

        <Input type='textarea' name='situation' maxLength={MAX_GOAL_TEXT} helpText={t("help.situation")} handleChange={handleChange} label={t("situation")} value={formData.situation} />

        <Input type='textarea' name='plan' maxLength={MAX_GOAL_TEXT} handleChange={handleChange} helpText={t("help.plan")} label={t("plan")} value={formData.plan} />
        <div className={styles.fieldRow}>
          <Select
            options={getAccessibleByOptions()}
            handleChange={handleChange}
            name='accessibleBy'
            helpText={t("help.goalAccessibleBy")}
            label={t("accessibleBy")}
            value={formData.accessibleBy}
          />
        </div>
        {formData.accessibleBy === "selectedGroups" && (
          <GroupAccess availableGroups={availableGroups} initiallySelected={formData.groupsWithAccess || []} setGroupsWithAccess={setGroupsWithAccess} />
        )}
        <Button>{t(isEdit ? "submitEdit" : "submit")}</Button>
      </form>
    </Wrapper>
  );
};

export default ImprovementGoalForm;
